import { useState, useMemo } from "react"
import { CalendarEventVisit, DayViewEvent as Event, Timesheet, Visit, VisitWorkareaStatusRecord, ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"
import DayViewEvent from "./DayViewEvent"
import DayViewConsultation from "./DayViewConsultation"
import DayViewTask from "./DayViewTask"
import { Button } from 'antd'
import { RightOutlined, DownOutlined } from "@ant-design/icons"
import { Dayjs } from 'dayjs'

type Props = {
  title: string
  events: Event[]
  consultations?: ConsultationCalendarEvent[]
  tasks?: TaskCalendarEvent[]
  workareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
  visits: Visit[]
  calendarEventVisits: CalendarEventVisit[]
  date: Dayjs
  hoveredEventId: number | null
  hoveredEventIds: number[]
  setHoveredEventId: (id: number | null) => void
  setHoveredEventIds: (ids: number[]) => void
  hoveredConsultationId?: number | null
  setHoveredConsultationId?: (id: number | null) => void
  hoveredTaskId?: number | null
  setHoveredTaskId?: (id: number | null) => void
  onConsultationClick?: (consultation: ConsultationCalendarEvent) => void
  onTaskClick?: (event: TaskCalendarEvent) => void
  onReorderClick?: () => void
  defaultExpanded?: boolean
  showTopBorder?: boolean
}

type CalendarItem =
  | { type: 'event' } & Event
  | { type: 'consultation' } & ConsultationCalendarEvent
  | { type: 'task' } & TaskCalendarEvent

const DayViewEventGroup = ({ title, events, consultations = [], tasks = [], workareaStatuses, timesheets, visits, calendarEventVisits, date, hoveredEventId, hoveredEventIds, setHoveredEventId, setHoveredEventIds, hoveredConsultationId, setHoveredConsultationId, hoveredTaskId, setHoveredTaskId, onConsultationClick, onTaskClick, onReorderClick, defaultExpanded = false, showTopBorder = false }: Props) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const sortedItems: CalendarItem[] = useMemo(() => {
    return [
      ...events.map((e): CalendarItem => ({ type: 'event', ...e })),
      ...consultations.map((c): CalendarItem => ({ type: 'consultation', ...c })),
      ...tasks.map((e): CalendarItem => ({ type: 'task', ...e })),
    ].sort((a, b) => {
      if (a.start === b.start) {
        return 0
      }
      return a.start > b.start ? 1 : -1
    })
  }, [events, consultations, tasks])

  const totalCount = events.length + consultations.length + tasks.length

  if (totalCount === 0) {
    return <></>
  }

  return (
    <div style={{ padding: '0 20px', backgroundColor: '#fff' }}>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          fontSize: '14px',
          fontWeight: '700',
          padding: '12px 0',
          color: '#333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          borderTop: showTopBorder ? '1px solid #e8e8e8' : 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#666' }}>
            {isExpanded ? <DownOutlined /> : <RightOutlined />}
          </span>
          <span>{title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {title === 'Planned' && onReorderClick && events.length >= 2 && (
            <Button type="default" size="small" onClick={(e) => { e.stopPropagation(); onReorderClick() }}>
              Reorder
            </Button>
          )}
          <span style={{
            color: '#333',
            border: '2px solid #bfbfbf',
            borderRadius: '12px',
            padding: '2px 10px',
            fontSize: '13px',
            fontWeight: '600'
          }}>{totalCount}</span>
        </div>
      </div>
      {isExpanded && (
        <div style={{ paddingBottom: '12px' }}>
          {sortedItems.map((item) => {
            if (item.type === 'event') {
              const isHovered = hoveredEventId === item.id || hoveredEventIds.includes(item.id)
              return (
                <DayViewEvent
                  key={`event-${item.id}`}
                  event={item}
                  workareaStatuses={workareaStatuses}
                  timesheets={timesheets}
                  visits={visits}
                  calendarEventVisits={calendarEventVisits}
                  date={date}
                  isHovered={isHovered}
                  onMouseEnter={() => {
                    setHoveredEventId(item.id)
                    setHoveredEventIds([])
                  }}
                  onMouseLeave={() => {
                    setHoveredEventId(null)
                    setHoveredEventIds([])
                  }}
                />
              )
            }
            if (item.type === 'task') {
              const isHovered = hoveredTaskId === item.id
              return (
                <DayViewTask
                  key={`task-${item.id}`}
                  event={item}
                  date={date}
                  isHovered={isHovered}
                  onMouseEnter={() => setHoveredTaskId?.(item.id)}
                  onMouseLeave={() => setHoveredTaskId?.(null)}
                  onClick={() => onTaskClick?.(item)}
                />
              )
            }
            if (item.type === 'consultation') {
              const isHovered = hoveredConsultationId === item.id
              return (
                <DayViewConsultation
                  key={`consultation-${item.id}`}
                  consultation={item}
                  date={date}
                  isHovered={isHovered}
                  onMouseEnter={() => setHoveredConsultationId?.(item.id)}
                  onMouseLeave={() => setHoveredConsultationId?.(null)}
                  onClick={() => onConsultationClick?.(item)}
                />
              )
            }
            return null
          })}
        </div>
      )}
    </div>
  )
}
export default DayViewEventGroup